"use client";

import { useEffect, useRef, useState } from "react";

export type DropdownOption = {
  value: string;
  label: string;
};

export function SearchDropdown({
  name,
  label,
  placeholder,
  defaultValue = "",
  options,
  icon,
}: {
  name: string;
  label: string;
  placeholder: string;
  defaultValue?: string;
  options: DropdownOption[];
  icon: React.ReactNode;
}) {
  const [selected, setSelected] = useState<string>(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === selected);
  const displayLabel = selectedOption ? selectedOption.label : placeholder;

  useEffect(() => {
    function handlePointerDown(e: MouseEvent | TouchEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handlePointerDown);
      document.addEventListener("touchstart", handlePointerDown);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  function handleSelect(value: string) {
    setSelected(value);
    setIsOpen(false);
  }

  return (
    <div ref={containerRef} className="public-search__item search-custom-dropdown">
      <span className="public-search__icon" aria-hidden="true">
        {icon}
      </span>

      <div className="public-search__field-body">
        <span className="public-search__field-label">{label}</span>
        <button
          type="button"
          className="search-dropdown-trigger"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="search-dropdown-trigger__text">{displayLabel}</span>
          <span className="search-dropdown-trigger__arrow" aria-hidden="true">
            ▾
          </span>
        </button>
      </div>

      <input type="hidden" name={name} value={selected} />

      {isOpen && (
        <div className="search-dropdown-menu" role="listbox">
          <div
            className={`search-dropdown-option ${
              selected === "" ? "search-dropdown-option--selected" : ""
            }`}
            onClick={() => handleSelect("")}
            role="option"
            aria-selected={selected === ""}
          >
            <span className="search-dropdown-option__check">
              {selected === "" ? "✓" : ""}
            </span>
            <span className="search-dropdown-option__text">{placeholder}</span>
          </div>

          {options.map((option) => (
            <div
              key={option.value}
              className={`search-dropdown-option ${
                selected === option.value ? "search-dropdown-option--selected" : ""
              }`}
              onClick={() => handleSelect(option.value)}
              role="option"
              aria-selected={selected === option.value}
            >
              <span className="search-dropdown-option__check">
                {selected === option.value ? "✓" : ""}
              </span>
              <span className="search-dropdown-option__text">{option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
